import { MarketType } from './market.domain';
import { Quote } from './quote.domain';

const SYMBOL_REG_EXP = /^[-@;\w.\s]+ ([Cc][Aa]|[Uu][Ss])$/;
const MUTUAL_FUND_REG_EXP = /^[\w]{6,8}$/;

const hasSuffix = (source: string, suffix: string): boolean => {
  const includesSuffix = source.indexOf(suffix, source.length - suffix.length) !== -1; // TODO replace with String.includes
  const includesSuffixDot = source.indexOf(suffix + '.') !== -1;
  return includesSuffix || includesSuffixDot;
};

export enum SymbolType {
  STOCK = 'STOCK',
  INDEX = 'INDEX',
  OPTION_UNDERLYING = 'OPTION_UNDERLYING',
  OPTION = 'OPTION',
  MUTUALFUND = 'MUTUALFUND',
  DEBENTURE = 'DEBENTURE',
  FIXED_INCOME = 'FIXED_INCOME',
  ETF = 'ETF',
  GIC = 'GIC',
  UNDEFINED = 'UNDEFINED',
  EQUITY = 'EQUITY'
}

type QuoteDetailFields =
  'ask' |
  'askSize' |
  'bid' |
  'bidSize' |
  'companyName' |
  'currency' |
  'highPrice' |
  'lastPrice' |
  'lotSize' |
  'lowPrice' |
  'openInterest' |
  'openPrice' |
  'percentChange' |
  'priceChange' |
  'securityType' |
  'symbol' |
  'volume' |
  'yearHigh' |
  'yearLow';

export type QuoteDetail = Required<Pick<Quote, QuoteDetailFields>>;

export interface TickerElements {
  contractExpiry?: string;
  strikePrice?: string;
  type?: string;
  underlyingCompany?: string;
}

export interface SecurityInfo {
  type: SymbolType;
  symbol: string;
  market: MarketType;
  symbolType?: SymbolType;
  displayTicker?: string; // used for options. TODO: fix on the backend
  quote?: QuoteDetail;
  tickerElements?: TickerElements; // underlyingCompany is only used,
  description?: string;
  tdInvestmentSavingsAccountFund?: boolean; // used for mutual fund symbol search
  fractionalBuy?: boolean;
  fractionalSell?: boolean;
}

export interface SecuritySymbol {
  symbol?: string; // empty for fixed income TODO: fix in rest layer
  tickerSymbol?: string;
  displaySymbol?: string;
  companySymbol?: string;
  description?: string;
  type?: SymbolType; // TODO: some calls return type instead of SymbolType. merge with symbolType
  symbolType?: SymbolType;
  subTypeCd?: SymbolType; // TODO: BE returns 'ETF' for ETFs, symbolType returned as 'STOCK' for ETFs
  valid?: boolean; // TODO: same as symbolError? May be introduce symbolStatus field instead?
  id?: number;
  marketType?: MarketType;
  market?: MarketType; // TODO: what is the difference with marketType
  exchange?: string;
  optionExpired?: boolean;
  call?: boolean;
  symbolError?: boolean;  // TODO - backend: Add symbol error instead of deriving on the backend. How this correlates with valid property
  mutualFund?: boolean;
  fractionalBuy?: boolean;
  fractionalSell?: boolean;
  toString(): string;
}

export class SecuritySymbolHelper {

  static isValid(symbol: string, symbolType?: SymbolType | Array<SymbolType>): boolean {
    if (symbolType !== undefined && !Array.isArray(symbolType)) {
      if (symbolType === SymbolType.MUTUALFUND) {
        return MUTUAL_FUND_REG_EXP.test(symbol);
      }
      if (symbolType === SymbolType.DEBENTURE) {
        return SecuritySymbolHelper.isDebenture(symbol);
      }
    }
    return  SYMBOL_REG_EXP.test(symbol);
  }

  static isDebenture(symbol: string | Pick<SecuritySymbol, 'symbol'>): boolean {
    let symbolUCase = '';
    if (symbol !== undefined) {
      if (typeof symbol === 'string') {
        symbolUCase = symbol.toUpperCase();
      } else if (symbol.symbol !== undefined) {
        symbolUCase = symbol.symbol.toUpperCase();
      }
      return hasSuffix(symbolUCase, '.DB')
        || hasSuffix(symbolUCase, '.NT')
        || hasSuffix(symbolUCase, '.NS')
        || hasSuffix(symbolUCase, '.NO');
    }
    return false;
  }

  static equals(s1: SecuritySymbol, s2: SecuritySymbol): boolean {
    const symbolsDefined = s1 !== undefined && s2 !== undefined && Boolean(s1.symbol) &&
      Boolean(s2.symbol) &&
      Boolean(s1.marketType) && Boolean(s2.marketType);

    if (symbolsDefined) {
      return (s1.symbol.toUpperCase() === s2.symbol.toUpperCase())
          && (s1.marketType.toUpperCase() === s2.marketType.toUpperCase());
    }
    return false;
  }

  // Parses string into a Symbol object
  // for mutual fund this returns object contains 'symbol' field plus 'market' field of empty string ''
  static parse<T>(symbolString: string): T | undefined {
    let s;
    let marketType;
    let symbol;
    if (symbolString !== undefined && typeof symbolString === 'string') {
      symbol = '';
      marketType = '';
      s = symbolString.trim().split(' ');
      // if OPTION
      if (s.length === 5) {
        symbol = s.splice(0, 4).join(' ');
        marketType = s[0];
      } else {
        if (s.length > 0) {
          symbol = s[0].toUpperCase();
        }
        if (s.length > 1) {
          marketType = s[1].toUpperCase();
        }
      }
      return { symbol, marketType, market: marketType } as T; // TODO - app is using marketType/market depending on component
    }
    return undefined;
  }
}

type SymbolSearchTypes = SymbolType.STOCK | SymbolType.MUTUALFUND | SymbolType.OPTION | SymbolType.INDEX;

export class SymbolItem {
  constructor(public symbolType: SymbolSearchTypes,
              public labelKey: string,
              public placeholderKey: string,
              public errorKey: string) {}
}
